import { PhysicalPadUI, usePadsStore } from '@/stores/usePadsStore'
import { VPadManager } from './VPadManager'
import { PadManagerEvent, PadManagerEventListener, PadManagerEventType, VPadState } from '@/interfaces/virtualPad_types'
import { VPad } from './VPad'

export class PadManager {
  private static readonly MAX_PADS = 4
  private static instance: PadManager | undefined
  private readonly vPadManager: VPadManager
  private readonly eventListeners = new Set<PadManagerEventListener>()

  /**
   * Initializes the pad manager
   */
  private constructor() {
    this.vPadManager = VPadManager.getInstance()

    this.vPadManager.setOnVPadRemoved((connectionId) => {
      this.emit({
        type: PadManagerEventType.VPAD_DISCONNECTED,
        connectionId
      })
    })
  }

  /**
   * Returns the PadManager instance, creating it if necessary
   * 
   * @returns The PadManager instance
   */
  public static getInstance(): PadManager {
    if (PadManager.instance === undefined) {
      PadManager.instance = new PadManager()
    }

    return PadManager.instance
  }

  /**
   * Adds a virtual pad to the available controller slots.
   *
   * Validates that there is an available slot, assigns an index
   * and delegates the virtual pad creation to VPadManager.
   *
   * @param sessionId Session identifier
   * @param connectionId Virtual pad connection identifier
   * @throws If the maximum number of gamepads was reached
   */
  public async addVPad(sessionId: string, connectionId: string): Promise<void> {
    if (!this.hasAvailableSlot()) {
      this.emit({
        type: PadManagerEventType.MAX_PADS_REACHED,
        connectionId
      })

      throw Object.assign(new Error('PAD MANAGER :: ADD VPAD :: Maximum number of gamepads reached'), {
        name: 'maxGamepadsReached',
      })
    }

    const index = this.getNextAvailableIndex()

    try {
      await this.vPadManager.createAndConnect(sessionId, connectionId, index)
      this.emit({
        type: PadManagerEventType.VPAD_CONNECTED,
        connectionId
      })
    } catch (error) {
      this.emit({
        type: PadManagerEventType.VPAD_ERROR,
        connectionId,
        error
      })
      throw error
    }
  }

  /**
   * Requests a virtual pad to leave and removes it locally
   *
   * @param connectionId Virtual pad connection identifier
   */
  public leaveVPad(connectionId: string): void {
    try {
      const left = this.vPadManager.leave(connectionId)

      if (!left) return

      this.emit({
        type: PadManagerEventType.VPAD_DISCONNECTED,
        connectionId
      })
    } catch (error) {
      this.emit({
        type: PadManagerEventType.VPAD_ERROR,
        connectionId,
        error
      })
    }
  }

  /**
   * Removes a virtual pad
   * 
   * This method is used when the remote peer has already left
   * or the server reports that the peer is no longer available
   *
   * The operation has no effect if the virtual pad has already been removed
   *
   * @param connectionId Virtual pad connection identifier
   */
  public removeVPad(connectionId: string): void {
    try {
      const removed = this.vPadManager.remove(connectionId)

      if (!removed) return

      this.emit({
        type: PadManagerEventType.VPAD_DISCONNECTED,
        connectionId
      })
    } catch (error) {
      this.emit({
        type: PadManagerEventType.VPAD_ERROR,
        connectionId,
        error
      })
    }
  }

  /**
   * Closes and removes all virtual pads
   */
  public clearVPads(): void {
    this.vPadManager.clear()
  }

  /**
   * Gets the list of virtual pads currently managed by the pad manager
   * @returns List of virtual pads
   */
  public getVPads(): VPad[] {
    return this.vPadManager.getVPadList()
  }

  /**
   * Gets the state of all virtual pads currently managed by the pad manager
   * @returns List of virtual pad states
   */
  public getStateVPads(): VPadState[] {
    return this.vPadManager.getVPadStateList()
  }

  /**
   * Updates the connected physical pads and reconciles all controllers.
   *
   * @param physicalPads Current physical gamepads
   */
  public setPhysicalPads(physicalPads: PhysicalPadUI[]): void {
    usePadsStore.getState().setPhysicalPads(physicalPads)
    this.reconcilePads()
  }

  /**
   * Returns whether another controller can be registered
   *
   * Virtual pads in the connecting state also occupy a slot
   *
   * @returns True if there is at least one available slot
   */
  public hasAvailableSlot(): boolean {
    const { physicalPads, vpads } = usePadsStore.getState()

    const totalPads =
      Object.keys(physicalPads).length +
      Object.keys(vpads).length

    return totalPads < PadManager.MAX_PADS
  }

  /**
  * Gets the first index that is not being used by either a physical gamepad or a virtual pad
  *
  * @returns First available controller index
  */
  public getNextAvailableIndex(): number {
    const { physicalPads, vpads } = usePadsStore.getState()

    const occupiedIndexes = new Set<number>()

    Object.values(physicalPads).forEach((gamepad) => {
      occupiedIndexes.add(+gamepad.index)
    })

    Object.values(vpads).forEach((vPad) => {
      occupiedIndexes.add(+vPad.index)
    })

    let index = 0
    while (occupiedIndexes.has(index)) {
      index++
    }

    return index
  }

  /**
   * Reconciles virtual pads after the physical gamepad list changes.
   *
   * Physical gamepads always have priority over virtual pads.
   */
  public reconcilePads(): void {
    let { physicalPads, vpads } = usePadsStore.getState()

    let totalPads =
      Object.keys(physicalPads).length +
      Object.keys(vpads).length

    // Remove virtual pads until the maximum number of controllers is respected
    while (totalPads > PadManager.MAX_PADS) {
      const removedConnectionId = this.vPadManager.removeToFreeSlot()

      // No more virtual pads can be removed
      if (removedConnectionId === null) break

      // Read the updated state after the removal
      const currentState = usePadsStore.getState()

      physicalPads = currentState.physicalPads
      vpads = currentState.vpads
      
      totalPads =
        Object.keys(currentState.physicalPads).length +
        Object.keys(currentState.vpads).length
    }

    // Keep track of the controller slots occupied by physical gamepads
    const physicalIndexes = new Set(
      Object.values(physicalPads).map(
        (gamepad) => Number(gamepad.index)
      )
    )

    Object.values(vpads).forEach((vPadUI) => {
      // This virtual pad does not conflict with a physical gamepad
      if (!physicalIndexes.has(Number(vPadUI.index))) return

      // Move the virtual pad to the first available slot
      const newIndex = this.getNextAvailableIndex()
      this.vPadManager.setIndex(vPadUI.connectionId, newIndex)
    })
  }

  /**
   * Applies a WebRTC answer to a virtual pad
   *
   * @param connectionId Virtual pad connection identifier
   * @param sdp Remote answer SDP
   */
  public async setVPadAnswer(connectionId: string, sdp: string): Promise<void> {
    await this.vPadManager.setAnswer(connectionId, sdp)
  }

  /**
   * Adds a remote ICE candidate to a virtual pad
   *
   * @param connectionId Virtual pad connection identifier
   * @param candidate Remote ICE candidate
   */
  public async addVPadIceCandidate(connectionId: string, candidate: RTCIceCandidateInit): Promise<void> {
    await this.vPadManager.addIceCandidate(connectionId, candidate)
  }

  /**
   * Subscribes to pad manager events.
   *
   * @param listener Event listener
   * @returns Function used to remove the listener
   */
  public subscribe(listener: PadManagerEventListener): () => void {
    this.eventListeners.add(listener)

    return () => {
      this.eventListeners.delete(listener)
    }
  }

  /**
   * Notifies all registered listeners about a pad manager event.
   *
   * @param event Event to notify
   */
  private emit(event: PadManagerEvent): void {
    this.eventListeners.forEach((listener) => {
      listener(event)
    })
  }
}